import React, { useState, useEffect, useRef } from 'react';
import { GetRequest, PostRequest } from 'components/common';
import { ContentPopup } from 'components/ContentPopup.jsx';
import SheetPreview from './SheetPreview';
import AirtablePreview from './AirtablePreview';
import GoogleSheetsPicker from './GoogleSheetsPicker';
import OneDrivePicker from './OneDrivePicker';
import { DataSourceConnectButtons } from '../DataSourceIcons';
import DataSourceLearnMore from 'components/quote_builder/DataSourceLearnMore';
import { useDataSourceOAuthOptional } from '../DataSourceOAuthContext';

const DataSourcePriceInput = ({
  value,
  onChange,
  currency,
  precision,
  dataSourceConfig,
  onDataSourceConfigChange,
  disabled,
  userUnitPriceId,
  attributeName
}) => {
  // Use context if available (within DataSourceOAuthProvider), otherwise null
  const oauthContext = useDataSourceOAuthOptional();

  // Local state for dataSources - synced from context if available
  const [localDataSources, setLocalDataSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null); // { id, name }
  const [cellReference, setCellReference] = useState('');
  const [fetchedValue, setFetchedValue] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [fetchingValue, setFetchingValue] = useState(false);
  const [localLastSpreadsheetId, setLocalLastSpreadsheetId] = useState(null);
  // Airtable-specific state
  const [bases, setBases] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedAirtableCell, setSelectedAirtableCell] = useState(null); // { recordId, recordName, fieldId, fieldName, value }
  // Google Sheets Picker state
  const [showGooglePicker, setShowGooglePicker] = useState(false);
  // OneDrive Picker state
  const [showOneDrivePicker, setShowOneDrivePicker] = useState(false);
  const popupRef = useRef();
  // Track if initial config has been loaded (for editing existing prices)
  const [configLoading, setConfigLoading] = useState(!!userUnitPriceId);

  // Use dataSources from context if available, otherwise use local state
  const dataSources = oauthContext?.dataSources || localDataSources;
  const lastSpreadsheetId = oauthContext?.lastSpreadsheetId || localLastSpreadsheetId;

  const isLinkedToDataSource = dataSourceConfig && dataSourceConfig.data_source_id;

  // Fetch data sources on mount (only if not using context)
  useEffect(() => {
    if (!oauthContext) {
      fetchDataSourcesLocal();
    }
  }, [oauthContext]);

  // Local fetch function (used when not in context)
  const fetchDataSourcesLocal = () => {
    GetRequest({
      url: '/quote-builder/data-sources',
      callback: (response) => {
        setLocalDataSources(response.data_sources || []);
        if (response.last_spreadsheet_id) {
          setLocalLastSpreadsheetId(response.last_spreadsheet_id);
        }
      }
    });
  };

  // Refetch function that works with or without context
  const refetchDataSources = () => {
    if (oauthContext) {
      return oauthContext.refetchDataSources();
    } else {
      fetchDataSourcesLocal();
      return Promise.resolve();
    }
  };

  useEffect(() => {
    // Config has been loaded from parent (whether linked or not)
    setConfigLoading(false);

    if (dataSourceConfig) {
      setSelectedDataSource(dataSourceConfig.data_source_id);
      // Check if this is an Airtable config (has base_id) or spreadsheet config
      if (dataSourceConfig.base_id) {
        setSelectedBase(dataSourceConfig.base_id);
        setSelectedTable(dataSourceConfig.table_id);
        setSelectedAirtableCell({
          recordId: dataSourceConfig.record_id,
          recordName: dataSourceConfig.record_name,
          fieldId: dataSourceConfig.field_id,
          fieldName: dataSourceConfig.field_name,
          value: dataSourceConfig.last_fetched_value
        });
      } else {
        setSelectedSpreadsheet(dataSourceConfig.spreadsheet_id);
        setSelectedSheet({
          id: dataSourceConfig.sheet_id,
          name: dataSourceConfig.sheet_name
        });
        setCellReference(dataSourceConfig.cell_reference);
      }
      setFetchedValue(dataSourceConfig.last_fetched_value);
    }
  }, [dataSourceConfig]);

  // When popup opens with existing config, fetch files based on source type
  useEffect(() => {
    if (showConfigPopup && dataSourceConfig && dataSourceConfig.data_source_id) {
      // Find the data source to determine its type
      const ds = dataSources.find(d => d.id == dataSourceConfig.data_source_id);
      const sourceType = ds ? ds.source_type : null;

      if (sourceType === 'airtable' || dataSourceConfig.base_id) {
        // Airtable data source - fetch bases
        fetchBases(dataSourceConfig.data_source_id);
        // Also fetch tables for the selected base
        if (dataSourceConfig.base_id) {
          setLoading(true);
          GetRequest({
            url: `/quote-builder/data-sources/${dataSourceConfig.data_source_id}/tables?base_id=${dataSourceConfig.base_id}`,
            callback: (data) => {
              setTables(data);
              setLoading(false);
            },
            422: () => setLoading(false)
          });
        }
      } else {
        // Google Sheets or Microsoft Excel - fetch spreadsheets
        fetchSpreadsheets(dataSourceConfig.data_source_id);
        // Also fetch sheets for the selected spreadsheet
        if (dataSourceConfig.spreadsheet_id) {
          setLoading(true);
          GetRequest({
            url: `/quote-builder/data-sources/${dataSourceConfig.data_source_id}/spreadsheet/${dataSourceConfig.spreadsheet_id}/sheets`,
            callback: (data) => {
              setSheets(data);
              // If we have a sheet_name but no sheet_id (legacy data), find the sheet by name
              if (dataSourceConfig.sheet_name && !dataSourceConfig.sheet_id) {
                const matchingSheet = data.find(s => s.name === dataSourceConfig.sheet_name);
                if (matchingSheet) {
                  setSelectedSheet({ id: matchingSheet.id, name: matchingSheet.name });
                }
              }
              setLoading(false);
            }
          });
        }
      }
    }
  }, [showConfigPopup]); // IMPORTANT: Removed dataSources from dependencies to prevent race conditions

  // Auto-select data source when there's only one available
  useEffect(() => {
    if (showConfigPopup && !dataSourceConfig && dataSources.length === 1 && !selectedDataSource) {
      handleDataSourceSelect(dataSources[0].id);
    }
  }, [showConfigPopup, dataSources.length, selectedDataSource, dataSourceConfig]);

  const getSelectedDataSourceType = () => {
    if (!selectedDataSource) return null;
    const ds = dataSources.find(d => d.id == selectedDataSource);
    return ds ? ds.source_type : null;
  };

  const fetchFilesForDataSource = (dataSourceId, sourceType) => {
    if (sourceType === 'google_sheets') {
      fetchSpreadsheets(dataSourceId);
    } else if (sourceType === 'microsoft_excel') {
      fetchWorkbooks(dataSourceId);
    } else if (sourceType === 'airtable') {
      fetchBases(dataSourceId);
    }
  };

  // OAuth handlers - use context if available, otherwise use local popup
  const handleConnectGoogleSheets = () => {
    if (typeof trackEvent === 'function') {
      trackEvent('data_source_oauth_initiated', { source_type: 'google_sheets' });
    }

    if (oauthContext) {
      oauthContext.initiateOAuth(
        '/quote-builder/data-sources/google-sheets/authorize',
        'google_sheets_oauth_complete',
        (result) => {
          if (result.success && result.dataSourceId) {
            setSelectedDataSource(result.dataSourceId);
            const newDataSource = result.dataSources.find(ds => ds.id == result.dataSourceId);
            if (newDataSource) {
              fetchFilesForDataSource(result.dataSourceId, newDataSource.source_type);
            }
          }
        }
      );
    } else {
      openOAuthPopupLocal('/quote-builder/data-sources/google-sheets/authorize', 'google_sheets_auth');
    }
  };

  const handleConnectMicrosoftExcel = () => {
    if (typeof trackEvent === 'function') {
      trackEvent('data_source_oauth_initiated', { source_type: 'microsoft_excel' });
    }

    if (oauthContext) {
      oauthContext.initiateOAuth(
        '/quote-builder/data-sources/microsoft-excel/authorize',
        'microsoft_excel_oauth_complete',
        (result) => {
          if (typeof trackEvent === 'function') {
            trackEvent('data_source_oauth_callback_received', {
              source_type: 'microsoft_excel',
              success: result.success,
              data_source_id: result.dataSourceId
            });
          }

          if (result.success && result.dataSourceId) {
            setSelectedDataSource(result.dataSourceId);
            // For a newly created data source, we know there are no workbooks yet
            // Open the OneDrive picker directly to let user select files
            setShowOneDrivePicker(true);

            if (typeof trackEvent === 'function') {
              trackEvent('data_source_onedrive_picker_opening', {
                data_source_id: result.dataSourceId
              });
            }
          }
        }
      );
    } else {
      openOAuthPopupLocal('/quote-builder/data-sources/microsoft-excel/authorize', 'microsoft_excel_auth');
    }
  };

  const handleConnectAirtable = () => {
    if (typeof trackEvent === 'function') {
      trackEvent('data_source_oauth_initiated', { source_type: 'airtable' });
    }

    if (oauthContext) {
      oauthContext.initiateOAuth(
        '/quote-builder/data-sources/airtable/authorize',
        'airtable_oauth_complete',
        (result) => {
          if (result.success && result.dataSourceId) {
            setSelectedDataSource(result.dataSourceId);
            const newDataSource = result.dataSources.find(ds => ds.id == result.dataSourceId);
            if (newDataSource) {
              fetchFilesForDataSource(result.dataSourceId, newDataSource.source_type);
            }
          }
        }
      );
    } else {
      openOAuthPopupLocal('/quote-builder/data-sources/airtable/authorize', 'airtable_auth');
    }
  };

  // Fallback for when not in context (shouldn't happen in normal use)
  const openOAuthPopupLocal = (url, name) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        fetchDataSourcesLocal();
      }
    }, 500);
  };

  const fetchSpreadsheets = (dataSourceId, autoSelectSpreadsheetId = null) => {
    if (dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/spreadsheets`,
        callback: (data) => {
          setSpreadsheets(data);
          setLoading(false);
          // Auto-open picker if no spreadsheets available (better UX)
          if (data.length === 0) {
            setShowGooglePicker(true);
            return;
          }
          // Auto-select if there's only one spreadsheet (better UX)
          if (data.length === 1 && !selectedSpreadsheet) {
            handleSpreadsheetSelect(data[0].id, dataSourceId);
            return;
          }
          // Auto-select the last used spreadsheet if it exists in the list
          const spreadsheetToSelect = autoSelectSpreadsheetId || lastSpreadsheetId;
          if (spreadsheetToSelect && !selectedSpreadsheet) {
            const matchingSpreadsheet = data.find(s => s.id === spreadsheetToSelect);
            if (matchingSpreadsheet) {
              // Pass dataSourceId explicitly since selectedDataSource may not be updated yet
              handleSpreadsheetSelect(spreadsheetToSelect, dataSourceId);
            }
          }
        }
      });
    }
  };

  const handleGooglePickerSpreadsheetsPicked = (pickedSpreadsheets) => {
    setShowGooglePicker(false);
    setShowConfigPopup(true);
    if (pickedSpreadsheets && pickedSpreadsheets.length > 0) {
      // Merge picked spreadsheets with existing ones (avoid duplicates)
      setSpreadsheets(prevSpreadsheets => {
        const existingIds = new Set(prevSpreadsheets.map(s => s.id));
        const newSpreadsheets = pickedSpreadsheets.filter(s => !existingIds.has(s.id));
        return [...prevSpreadsheets, ...newSpreadsheets];
      });
      // Auto-select the last picked spreadsheet (most recently selected by user)
      const lastPicked = pickedSpreadsheets[pickedSpreadsheets.length - 1];
      setSelectedSpreadsheet(lastPicked.id);
      // Fetch sheets for the selected spreadsheet
      const dataSourceId = selectedDataSource;
      if (lastPicked.id && dataSourceId) {
        setLoading(true);
        GetRequest({
          url: `/quote-builder/data-sources/${dataSourceId}/spreadsheet/${lastPicked.id}/sheets`,
          callback: (data) => {
            setSheets(data);
            if (data.length > 0) {
              setSelectedSheet({ id: data[0].id, name: data[0].name });
            }
            setLoading(false);
          }
        });
      }
    } else {
      // If no spreadsheets returned, refresh from backend
      fetchSpreadsheets(selectedDataSource);
    }
  };

  const handleOneDrivePickerWorkbooksPicked = (pickedWorkbooks) => {
    setShowOneDrivePicker(false);
    setShowConfigPopup(true);
    if (pickedWorkbooks && pickedWorkbooks.length > 0) {
      // Merge picked workbooks with existing ones (avoid duplicates)
      setSpreadsheets(previousSpreadsheets => {
        const existingIds = new Set(previousSpreadsheets.map(s => s.id));
        const newWorkbooks = pickedWorkbooks.filter(w => !existingIds.has(w.id));
        return [...previousSpreadsheets, ...newWorkbooks];
      });
      // Auto-select the last picked workbook (most recently selected by user)
      const lastPicked = pickedWorkbooks[pickedWorkbooks.length - 1];
      setSelectedSpreadsheet(lastPicked.id);
      // Fetch worksheets for the selected workbook
      const dataSourceId = selectedDataSource;
      if (lastPicked.id && dataSourceId) {
        setLoading(true);
        GetRequest({
          url: `/quote-builder/data-sources/${dataSourceId}/workbook/${lastPicked.id}/worksheets`,
          callback: (data) => {
            setSheets(data);
            if (data.length > 0) {
              setSelectedSheet({ id: data[0].id, name: data[0].name });
            }
            setLoading(false);
          }
        });
      }
    } else {
      // If no workbooks returned, refresh from backend
      fetchWorkbooks(selectedDataSource);
    }
  };

  const fetchWorkbooks = (dataSourceId, autoSelectWorkbookId = null) => {
    if (dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/workbooks`,
        callback: (data) => {
          setSpreadsheets(data); // Reuse spreadsheets state for workbooks
          setLoading(false);
          // Auto-open picker if no workbooks available (better UX)
          if (data.length === 0) {
            setShowOneDrivePicker(true);
            return;
          }
          // Auto-select if there's only one workbook (better UX)
          if (data.length === 1 && !selectedSpreadsheet) {
            handleWorkbookSelect(data[0].id, dataSourceId);
            return;
          }
          // Auto-select the last used workbook if it exists in the list
          const workbookToSelect = autoSelectWorkbookId || lastSpreadsheetId;
          if (workbookToSelect && !selectedSpreadsheet) {
            const matchingWorkbook = data.find(s => s.id === workbookToSelect);
            if (matchingWorkbook) {
              handleWorkbookSelect(workbookToSelect, dataSourceId);
            }
          }
        }
      });
    }
  };

  const handleDataSourceSelect = (dataSourceId) => {
    setSelectedDataSource(dataSourceId);
    // Clear spreadsheet state (Google Sheets / Excel)
    setSpreadsheets([]);
    setSheets([]);
    setSelectedSpreadsheet(null);
    setSelectedSheet(null);
    setCellReference('');
    // Clear Airtable state
    setBases([]);
    setTables([]);
    setSelectedBase(null);
    setSelectedTable(null);
    setSelectedAirtableCell(null);
    // Clear common state
    setFetchedValue(null);
    setFetchError(null);

    const ds = dataSources.find(d => d.id == dataSourceId);
    if (ds) {
      fetchFilesForDataSource(dataSourceId, ds.source_type);
    }

    if (typeof trackEvent === 'function') {
      trackEvent('data_source_selected', { source_type: ds ? ds.source_type : null });
    }
  };

  const handleRefreshSpreadsheets = () => {
    if (selectedDataSource) {
      setSpreadsheets([]);
      const ds = dataSources.find(d => d.id == selectedDataSource);
      if (ds) {
        fetchFilesForDataSource(selectedDataSource, ds.source_type);
      }
    }
  };

  const handleSpreadsheetSelect = (spreadsheetId, dataSourceIdOverride = null) => {
    setSelectedSpreadsheet(spreadsheetId);
    setSheets([]);
    setSelectedSheet(null);
    setFetchedValue(null);
    setFetchError(null);

    const dataSourceId = dataSourceIdOverride || selectedDataSource;
    if (spreadsheetId && dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/spreadsheet/${spreadsheetId}/sheets`,
        callback: (data) => {
          setSheets(data);
          // Auto-select the first sheet to show the preview immediately
          if (data.length > 0) {
            setSelectedSheet({ id: data[0].id, name: data[0].name });
          }
          setLoading(false);
        }
      });
    }
  };

  const handleWorkbookSelect = (workbookId, dataSourceIdOverride = null) => {
    setSelectedSpreadsheet(workbookId);
    setSheets([]);
    setSelectedSheet(null);
    setFetchedValue(null);
    setFetchError(null);

    const dataSourceId = dataSourceIdOverride || selectedDataSource;
    if (workbookId && dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/workbook/${workbookId}/worksheets`,
        callback: (data) => {
          setSheets(data);
          // Auto-select the first worksheet to show the preview immediately
          if (data.length > 0) {
            setSelectedSheet({ id: data[0].id, name: data[0].name });
          }
          setLoading(false);
        }
      });
    }
  };

  const handleFileSelect = (fileId, dataSourceIdOverride = null) => {
    const dataSourceId = dataSourceIdOverride || selectedDataSource;
    const ds = dataSources.find(d => d.id == dataSourceId);
    if (ds && ds.source_type === 'microsoft_excel') {
      handleWorkbookSelect(fileId, dataSourceId);
    } else if (ds && ds.source_type === 'airtable') {
      handleBaseSelect(fileId, dataSourceId);
    } else {
      handleSpreadsheetSelect(fileId, dataSourceId);
    }
  };

  // Airtable-specific methods
  const fetchBases = (dataSourceId, autoSelectBaseId = null) => {
    if (dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/bases`,
        callback: (data) => {
          setBases(data);
          setSpreadsheets([]); // Clear spreadsheets when switching to Airtable
          setLoading(false);
          // Auto-select if there's only one base (better UX)
          if (data.length === 1 && !selectedBase) {
            handleBaseSelect(data[0].id, dataSourceId);
            return;
          }
          // Auto-select the last used base if it exists in the list
          const baseToSelect = autoSelectBaseId || lastSpreadsheetId;
          if (baseToSelect && !selectedBase) {
            const matchingBase = data.find(b => b.id === baseToSelect);
            if (matchingBase) {
              handleBaseSelect(baseToSelect, dataSourceId);
            }
          }
        }
      });
    }
  };

  const handleBaseSelect = (baseId, dataSourceIdOverride = null) => {
    setSelectedBase(baseId);
    setTables([]);
    setSelectedTable(null);
    setSelectedAirtableCell(null);
    setFetchedValue(null);
    setFetchError(null);

    const dataSourceId = dataSourceIdOverride || selectedDataSource;
    if (baseId && dataSourceId) {
      setLoading(true);
      GetRequest({
        url: `/quote-builder/data-sources/${dataSourceId}/base/${baseId}/tables`,
        callback: (data) => {
          setTables(data);
          // Auto-select the first table to show the preview immediately
          if (data.length > 0) {
            setSelectedTable(data[0].id);
          }
          setLoading(false);
        }
      });
    }
  };

  const handleTableSelect = (table) => {
    setSelectedTable(table.id);
    setSelectedAirtableCell(null);
    setFetchedValue(null);
    setFetchError(null);
  };

  const handleAirtableCellSelect = (cellData) => {
    setSelectedAirtableCell(cellData);
    setFetchedValue(cellData.value);
    setFetchError(null);
  };

  const handleSaveConfig = () => {
    const sourceType = getSelectedDataSourceType();

    if (sourceType === 'airtable') {
      // Airtable save config
      if (!selectedDataSource || !selectedBase || !selectedTable || !selectedAirtableCell) {
        return;
      }

      const selectedBaseObj = bases.find(b => b.id === selectedBase);
      const baseName = selectedBaseObj ? selectedBaseObj.name : null;
      const selectedTableObj = tables.find(t => t.id === selectedTable);
      const tableName = selectedTableObj ? selectedTableObj.name : null;

      const config = {
        data_source_id: selectedDataSource,
        base_id: selectedBase,
        base_name: baseName,
        table_id: selectedTable,
        table_name: tableName,
        record_id: selectedAirtableCell.recordId,
        record_name: selectedAirtableCell.recordName,
        field_id: selectedAirtableCell.fieldId,
        field_name: selectedAirtableCell.fieldName,
        last_fetched_value: fetchedValue,
        last_fetched_at: new Date().toISOString()
      };

      onDataSourceConfigChange(config);
    } else {
      // Google Sheets / Microsoft Excel save config
      if (!selectedDataSource || !selectedSpreadsheet || !selectedSheet || !cellReference) {
        return;
      }

      // Find the spreadsheet name from the list
      const selectedSpreadsheetObj = spreadsheets.find(s => s.id === selectedSpreadsheet);
      const spreadsheetName = selectedSpreadsheetObj ? selectedSpreadsheetObj.name : null;

      const config = {
        data_source_id: selectedDataSource,
        spreadsheet_id: selectedSpreadsheet,
        spreadsheet_name: spreadsheetName,
        sheet_id: selectedSheet.id,
        sheet_name: selectedSheet.name, // Keep name for display purposes
        cell_reference: cellReference,
        last_fetched_value: fetchedValue,
        last_fetched_at: new Date().toISOString()
      };

      onDataSourceConfigChange(config);
    }

    if (fetchedValue !== null) {
      // Remove currency symbols, commas, and whitespace before parsing
      const cleanedValue = String(fetchedValue).replace(/[^0-9.-]/g, '');
      const numericValue = parseFloat(cleanedValue);
      if (!isNaN(numericValue)) {
        onChange({ target: { value: numericValue.toFixed(precision || 2) } });
      }
    }

    if (typeof trackEvent === 'function') {
      trackEvent('data_source_config_saved', { source_type: sourceType });
    }

    setShowConfigPopup(false);
  };

  const handleUnlinkDataSource = () => {
    if (typeof trackEvent === 'function') {
      trackEvent('data_source_unlinked', { attribute_name: attributeName });
    }

    if (!userUnitPriceId || !attributeName) {
      // Fallback to just updating local state if we don't have the IDs
      onDataSourceConfigChange(null);
      setSelectedDataSource(null);
      setSelectedSpreadsheet(null);
      setSelectedSheet(null);
      setCellReference('');
      setFetchedValue(null);
      return;
    }

    // Make API call to unlink the data source immediately
    PostRequest({
      url: `/quote-builder/user_unit_prices/${userUnitPriceId}/unlink_data_source`,
      data: { attribute_name: attributeName },
      callback: () => {
        onDataSourceConfigChange(null);
        setSelectedDataSource(null);
        setSelectedSpreadsheet(null);
        setSelectedSheet(null);
        setCellReference('');
        setFetchedValue(null);
      },
      400: (data) => {
        alert(data.errors || 'Failed to unlink data source');
      }
    });
  };

  const handleRefresh = () => {
    if (isLinkedToDataSource) {
      if (typeof trackEvent === 'function') {
        trackEvent('data_source_value_refreshed', { data_source_id: dataSourceConfig.data_source_id });
      }

      setFetchingValue(true);

      // Determine the request data based on config type (Airtable vs spreadsheet)
      const requestData = dataSourceConfig.base_id
        ? {
            base_id: dataSourceConfig.base_id,
            table_id: dataSourceConfig.table_id,
            record_id: dataSourceConfig.record_id,
            field_id: dataSourceConfig.field_id
          }
        : {
            spreadsheet_id: dataSourceConfig.spreadsheet_id,
            sheet_id: dataSourceConfig.sheet_id,
            sheet_name: dataSourceConfig.sheet_name,
            cell_reference: dataSourceConfig.cell_reference
          };

      PostRequest({
        url: `/quote-builder/data-sources/${dataSourceConfig.data_source_id}/fetch-cell`,
        data: requestData,
        callback: (data) => {
          setFetchingValue(false);
          if (!data.error && data.value !== null) {
            // Remove currency symbols, commas, and whitespace before parsing
            const cleanedValue = String(data.value).replace(/[^0-9.-]/g, '');
            const numericValue = parseFloat(cleanedValue);
            if (!isNaN(numericValue)) {
              onChange({ target: { value: numericValue.toFixed(precision || 2) } });
              onDataSourceConfigChange({
                ...dataSourceConfig,
                last_fetched_value: data.value,
                last_fetched_at: data.fetched_at
              });
            }
          }
        }
      });
    }
  };

  const getDataSourceName = () => {
    if (!dataSourceConfig) return '';
    const ds = dataSources.find(d => d.id === dataSourceConfig.data_source_id);
    return ds ? ds.name : '';
  };

  // Localized, relative "x minutes ago" for the last successful sync, so the user can see the
  // linked value is being kept fresh. Returns null when there is no usable timestamp.
  const formatTimeAgo = (isoString) => {
    if (!isoString) return null;
    const then = new Date(isoString).getTime();
    if (isNaN(then)) return null;

    const secondsAgo = Math.round((Date.now() - then) / 1000);
    const units = [
      ['year', 31536000],
      ['month', 2592000],
      ['week', 604800],
      ['day', 86400],
      ['hour', 3600],
      ['minute', 60],
      ['second', 1]
    ];

    for (const [unit, secondsInUnit] of units) {
      if (Math.abs(secondsAgo) >= secondsInUnit || unit === 'second') {
        const amount = Math.round(secondsAgo / secondsInUnit);
        try {
          const locale = (window.I18n && window.I18n.locale) || undefined;
          return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-amount, unit);
        } catch (error) {
          return new Date(isoString).toLocaleString();
        }
      }
    }
    return null;
  };

  const configPopupContent = (
    <div className="data-source-config" style={{ textAlign: 'left' }}>
      <h3>{t('quote_builder.configure_data_source')}</h3>
      <p className="text-muted" style={{ marginTop: '4px' }}>{t('quote_builder.link_to_data_source_hint')}</p>
      <br />

      {dataSources.length === 0 ? (
        <div className="no-data-sources">
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
            <h4 style={{ marginTop: 0, marginBottom: '10px', }}>{t('quote_builder.getting_started')}</h4>
            <p style={{ marginBottom: 0, color: '#666' }} dangerouslySetInnerHTML={{ __html: t('quote_builder.data_sources_getting_started_text') }} />
          </div>
          <DataSourceConnectButtons
            onConnectGoogleSheets={handleConnectGoogleSheets}
            onConnectMicrosoftExcel={handleConnectMicrosoftExcel}
            onConnectAirtable={handleConnectAirtable}
          />
        </div>
      ) : (
        <>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>{t('quote_builder.data_source')}</label>
            <select
              className="form-control"
              style={{ flex: '1 1 auto' }}
              value={selectedDataSource || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '__add_google_sheets__') {
                  handleConnectGoogleSheets();
                } else if (value === '__add_microsoft_excel__') {
                  handleConnectMicrosoftExcel();
                } else if (value === '__add_airtable__') {
                  handleConnectAirtable();
                } else {
                  handleDataSourceSelect(value);
                }
              }}
            >
              <option value="">{t('quote_builder.select_data_source')}</option>
              {dataSources.map(ds => (
                <option key={ds.id} value={ds.id}>{ds.name}</option>
              ))}
              <option disabled>──────────</option>
              <option value="__add_google_sheets__">+ {t('quote_builder.add_google_sheets_source')}</option>
              <option value="__add_microsoft_excel__">+ {t('quote_builder.add_microsoft_excel_source')}</option>
              <option value="__add_airtable__">+ {t('quote_builder.add_airtable_source')}</option>
            </select>
          </div>

          {selectedDataSource && getSelectedDataSourceType() === 'airtable' && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>{t('quote_builder.base')}</label>
              {loading ? (
                <span><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</span>
              ) : (
                <select
                  className="form-control"
                  style={{ flex: '1 1 auto' }}
                  value={selectedBase || ''}
                  onChange={(e) => handleBaseSelect(e.target.value)}
                >
                  <option value="">{t('quote_builder.select_base')}</option>
                  {bases.map(base => (
                    <option key={base.id} value={base.id}>{base.name}</option>
                  ))}
                </select>
              )}
              <small style={{ whiteSpace: 'nowrap' }}>
                <a href="#" onClick={(e) => { e.preventDefault(); fetchBases(selectedDataSource); }}>
                  <i className="fa fa-refresh"></i> {t('quote_builder.refresh_file_list')}
                </a>
              </small>
            </div>
          )}

          {selectedDataSource && getSelectedDataSourceType() !== 'airtable' && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>
                {getSelectedDataSourceType() === 'microsoft_excel'
                  ? t('quote_builder.workbook')
                  : t('quote_builder.spreadsheet')}
              </label>
              {loading ? (
                <span><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</span>
              ) : (
                <>
                  <select
                    className="form-control"
                    style={{ flex: '1 1 auto' }}
                    value={selectedSpreadsheet || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '__add_new_spreadsheet__') {
                        setShowGooglePicker(true);
                      } else if (value === '__add_new_workbook__') {
                        setShowOneDrivePicker(true);
                      } else {
                        handleFileSelect(value);
                      }
                    }}
                  >
                    <option value="">
                      {getSelectedDataSourceType() === 'microsoft_excel'
                        ? t('quote_builder.select_workbook')
                        : t('quote_builder.select_spreadsheet')}
                    </option>
                    {spreadsheets.map(ss => (
                      <option key={ss.id} value={ss.id}>{ss.name}</option>
                    ))}
                    {getSelectedDataSourceType() === 'google_sheets' && (
                      <>
                        <option disabled>──────────</option>
                        <option value="__add_new_spreadsheet__">+ {t('quote_builder.add_spreadsheet')}</option>
                      </>
                    )}
                    {getSelectedDataSourceType() === 'microsoft_excel' && (
                      <>
                        <option disabled>──────────</option>
                        <option value="__add_new_workbook__">+ {t('quote_builder.add_workbook')}</option>
                      </>
                    )}
                  </select>
                  {getSelectedDataSourceType() === 'google_sheets' && (
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={() => setShowGooglePicker(true)}
                      title={t('quote_builder.add_spreadsheet')}
                    >
                      <i className="fa fa-plus"></i>
                    </button>
                  )}
                  {getSelectedDataSourceType() === 'microsoft_excel' && (
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={() => setShowOneDrivePicker(true)}
                      title={t('quote_builder.add_workbook')}
                    >
                      <i className="fa fa-plus"></i>
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {selectedSheet && getSelectedDataSourceType() !== 'airtable' && (
            <div className="form-group">
              <SheetPreview
                dataSourceId={selectedDataSource}
                spreadsheetId={selectedSpreadsheet}
                sheetId={selectedSheet.id}
                sheetName={selectedSheet.name}
                sheets={sheets}
                onSheetSelect={(sheet) => {
                  setSelectedSheet({ id: sheet.id, name: sheet.name });
                  setCellReference('');
                  setFetchedValue(null);
                  setFetchError(null);
                }}
                selectedCell={cellReference}
                onCellSelect={(cellRef, cellValue) => {
                  setCellReference(cellRef);
                  setFetchedValue(cellValue);
                  setFetchError(null);
                }}
              />
            </div>
          )}

          {selectedTable && getSelectedDataSourceType() === 'airtable' && (
            <div className="form-group">
              <AirtablePreview
                dataSourceId={selectedDataSource}
                baseId={selectedBase}
                tableId={selectedTable}
                tables={tables}
                onTableSelect={handleTableSelect}
                selectedFieldId={selectedAirtableCell?.fieldId}
                selectedRecordId={selectedAirtableCell?.recordId}
                onCellSelect={handleAirtableCellSelect}
              />
            </div>
          )}

          {fetchError && (
            <div className="alert alert-danger">
              <i className="fa fa-exclamation-circle"></i> {fetchError}
            </div>
          )}

          {fetchedValue !== null && !fetchError && getSelectedDataSourceType() === 'airtable' && selectedAirtableCell && (
            <div className="alert alert-success">
              <strong>{t('quote_builder.selected_field')}:</strong> {selectedAirtableCell.fieldName} ({selectedAirtableCell.recordName}), <strong>{t('quote_builder.fetched_value')}:</strong> {fetchedValue} {currency}
            </div>
          )}

          {fetchedValue !== null && !fetchError && getSelectedDataSourceType() !== 'airtable' && (
            <div className="alert alert-success">
              <strong>{t('quote_builder.selected_cell')}:</strong> {cellReference}, <strong>{t('quote_builder.fetched_value')}:</strong> {fetchedValue} {currency}
            </div>
          )}

          <div className="form-actions" style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveConfig}
              disabled={!fetchedValue}
            >
              {t('quote_builder.save_and_use_value')}
            </button>
            <button
              type="button"
              className="btn btn-default"
              onClick={() => setShowConfigPopup(false)}
            >
              {t('quote_builder.cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="data-source-price-input">
      {configLoading ? (
        <div className="loading-data-source">
          <small>
            <i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}
          </small>
        </div>
      ) : isLinkedToDataSource ? (
        <div className="linked-data-source">
          <div className="data-source-info">
            <small>
              <i className="fa fa-link"></i> {getDataSourceName()} - {dataSourceConfig.base_id
                ? `${dataSourceConfig.table_name} > ${dataSourceConfig.field_name} (${dataSourceConfig.record_name})`
                : `${dataSourceConfig.sheet_name}!${dataSourceConfig.cell_reference}`}
              {' '}
              <a href="#" onClick={(e) => { e.preventDefault(); handleRefresh(); }}>
                {fetchingValue ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-refresh"></i>}
              </a>
              {' | '}
              <a href="#" onClick={(e) => { e.preventDefault(); setShowConfigPopup(true); }}>
                {t('quote_builder.edit')}
              </a>
              {' | '}
              <a href="#" onClick={(e) => { e.preventDefault(); handleUnlinkDataSource(); }}>
                {t('quote_builder.unlink')}
              </a>
            </small>
            {dataSourceConfig.last_fetched_at && formatTimeAgo(dataSourceConfig.last_fetched_at) && (
              <div>
                <small className="text-muted" title={new Date(dataSourceConfig.last_fetched_at).toLocaleString()}>
                  <i className="fa fa-clock-o"></i> {t('quote_builder.last_synced')} {formatTimeAgo(dataSourceConfig.last_fetched_at)}
                </small>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="manual-entry">
          <small>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (typeof trackEvent === 'function') {
                  trackEvent('data_source_link_clicked', { attribute_name: attributeName });
                }
                setShowConfigPopup(true);
              }}
            >
              <i className="fa fa-link"></i> {t('quote_builder.link_to_data_source')}
            </a>
            {' '}
            <DataSourceLearnMore />
          </small>
        </div>
      )}

      {showConfigPopup && (
        <ContentPopup
          myRef={popupRef}
          autoOpen={true}
          content={configPopupContent}
          cancelButton={false}
          beforeClose={() => setShowConfigPopup(false)}
        />
      )}

      {showGooglePicker && selectedDataSource && (
        <GoogleSheetsPicker
          dataSourceId={selectedDataSource}
          onSpreadsheetsPicked={handleGooglePickerSpreadsheetsPicked}
          onCancel={() => {
            setShowGooglePicker(false);
            setShowConfigPopup(true);
          }}
        />
      )}

      {showOneDrivePicker && selectedDataSource && (
        <OneDrivePicker
          dataSourceId={selectedDataSource}
          onWorkbooksPicked={handleOneDrivePickerWorkbooksPicked}
          onCancel={() => {
            setShowOneDrivePicker(false);
            setShowConfigPopup(true);
          }}
        />
      )}
    </div>
  );
};

export default DataSourcePriceInput;
